/**
 * Service to manage the user's preference for reimagined views.
 *
 * When the `reimaginedAccountActivity` feature flag is enabled (staging/local),
 * the reimagined views are ON by default. Users can toggle back to legacy views
 * via a sidebar switch. The preference is stored in localStorage.
 *
 * When the flag is OFF (production), reimagined views are always disabled
 * regardless of localStorage.
 */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type FeaturesService from 'tio-common/services/features';

const STORAGE_KEY = 'reimagined-account-activity';

export default class ReimaginedViewService extends Service {
  @service declare features: FeaturesService;

  @tracked private _userPreference: boolean | null = null;

  constructor(owner: object) {
    // @ts-expect-error owner type
    super(owner);
    this._loadPreference();
  }

  /** Whether the feature flag allows reimagined views at all */
  get flagEnabled(): boolean {
    return this.features.isEnabled('reimaginedAccountActivity');
  }

  /**
   * Whether reimagined views should render.
   * - If feature flag is OFF → always false
   * - If user has a stored preference → use it
   * - Otherwise → default to ON (flag is on = new experience by default)
   */
  get isEnabled(): boolean {
    if (!this.flagEnabled) {
      return false;
    }
    if (this._userPreference !== null) {
      return this._userPreference;
    }
    // Default: reimagined ON when flag is enabled
    return true;
  }

  /** Toggle between reimagined and legacy views */
  toggle = () => {
    const newValue = !this.isEnabled;
    this._userPreference = newValue;
    localStorage.setItem(STORAGE_KEY, String(newValue));
  };

  private _loadPreference() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      this._userPreference = stored === 'true';
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'reimagined-view': ReimaginedViewService;
  }
}
